/* Heritage Quick View — rich modal driven by the Section Rendering API.
   Fetches /products/{handle}?section_id=quick-view-product (server-rendered Liquid),
   injects it, then wires gallery / variants / quantity / favorites / add-to-cart. */
(function () {
  'use strict';

  var SECTION_ID = 'quick-view-product';
  var FAV_KEY = 'heritage:favorites';

  var htmlCache = {};
  var modal = null;
  var els = null;

  // --- Money formatting (mirrors Shopify's `| money` using shop.money_format) ---
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '{{ amount }}';

    function defaultTo(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    switch ((fmt.match(placeholderRegex) || [])[1]) {
      case 'amount':
        value = defaultTo(cents, 2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = defaultTo(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = defaultTo(cents, 0, ' ', '');
        break;
      case 'amount_with_apostrophe_separator':
        value = defaultTo(cents, 2, "'", '.');
        break;
      default:
        value = defaultTo(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  // ---------------- Modal lifecycle ----------------
  function getModal() {
    if (modal) return modal;
    modal = document.getElementById('quick-view-modal');
    if (!modal) return null;
    els = {
      panel: modal.querySelector('.qv-modal__panel'),
      loading: modal.querySelector('[data-qv-loading]'),
      content: modal.querySelector('[data-qv-content]'),
    };
    // Backdrop + Esc close (delegated close buttons handled after inject)
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-qv-close]')) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
      else if (e.key === 'Tab' && !modal.hidden) trapFocus(e);
    });
    return modal;
  }

  var lastFocused = null;

  function openModal() {
    var m = getModal();
    if (!m) return;
    lastFocused = document.activeElement;
    m.hidden = false;
    m.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { m.classList.add('is-open'); });
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    setTimeout(function () {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }, 250);
  }

  function focusables() {
    if (!modal) return [];
    return Array.prototype.slice.call(
      modal.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) { return el.offsetParent !== null; });
  }

  function trapFocus(e) {
    var f = focusables();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  // ---------------- Fetch + inject ----------------
  function fetchSection(handle) {
    if (htmlCache[handle]) return Promise.resolve(htmlCache[handle]);
    var url = '/products/' + handle + '?section_id=' + SECTION_ID;
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Quick view load failed: ' + res.status);
      return res.text();
    }).then(function (html) {
      htmlCache[handle] = html;
      return html;
    });
  }

  // ---------------- Per-instance state ----------------
  var state = null; // { root, variants, currentVariant, moneyFormat, ... }

  function wireContent() {
    var root = els.content.querySelector('[data-qv-root]');
    if (!root) return;

    var variants = [];
    try {
      var dataEl = root.querySelector('[data-qv-variants]');
      variants = JSON.parse(dataEl.textContent.trim());
    } catch (err) { variants = []; }

    var current = variants.filter(function (v) { return v.available; })[0] || variants[0] || null;

    state = {
      root: root,
      variants: variants,
      current: current,
      selected: current ? current.options.slice() : [],
      moneyFormat: root.dataset.qvMoneyFormat || '{{ amount }}',
      percent: parseInt(root.dataset.qvPercent || '0', 10),
      isCart: root.dataset.qvIsCart === '1',
      note: root.dataset.qvNote || '',
      color: root.dataset.qvColor || '#1F7A36',
      productUrl: root.dataset.qvProductUrl || '',
    };

    // Gallery thumbs
    root.querySelectorAll('[data-qv-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () { activateThumb(thumb.dataset.mediaId, thumb.dataset.full); });
    });

    // Option buttons
    root.querySelectorAll('[data-qv-opt-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectOption(parseInt(btn.dataset.optionPosition, 10), btn.dataset.optionValue);
      });
    });

    // Quantity stepper
    var qtyInput = root.querySelector('[data-qv-qty]');
    var minus = root.querySelector('[data-qv-qty-minus]');
    var plus = root.querySelector('[data-qv-qty-plus]');
    if (minus) minus.addEventListener('click', function () { stepQty(-1); });
    if (plus) plus.addEventListener('click', function () { stepQty(1); });
    if (qtyInput) qtyInput.addEventListener('change', function () { normalizeQty(); });

    // Favorites
    var favBtn = root.querySelector('[data-qv-fav]');
    if (favBtn) {
      syncFav(favBtn);
      favBtn.addEventListener('click', function () { toggleFav(favBtn); });
    }

    // Add to cart
    var atc = root.querySelector('[data-qv-atc]');
    if (atc) atc.addEventListener('click', handleAddToCart);

    // Linked colors ("Diğer Renkleri")
    initLinkedColors(root);
  }

  // ---------------- Linked colors ("Diğer Renkleri") ----------------
  // The colors live inside a <product-recommendations> web component that
  // normally lazy-loads via IntersectionObserver. Inside the modal its wrapper
  // starts display:none, so the observer never fires (a display:none element
  // never intersects) — a deadlock. We fetch the recommendations directly here,
  // then reveal the block (or drop it when the product has no linked colors).
  function initLinkedColors(root) {
    var wrap = root.querySelector('[data-qv-colors]');
    if (!wrap) return;
    var pr = wrap.querySelector('product-recommendations');
    if (!pr) { wrap.remove(); return; }

    // Stop the component's own observer so it can't re-fetch/overwrite later.
    if (pr.observer && typeof pr.observer.disconnect === 'function') pr.observer.disconnect();

    var baseUrl = pr.dataset.url;
    var productId = pr.dataset.productId;
    var sectionId = pr.dataset.sectionId;
    if (!baseUrl || !productId || !sectionId) { wrap.remove(); return; }

    fetch(baseUrl + '&product_id=' + productId + '&section_id=' + sectionId, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : Promise.reject(res.status); })
      .then(function (text) {
        var tmp = document.createElement('div');
        tmp.innerHTML = text;
        var rec = tmp.querySelector('product-recommendations');
        var hasItems = tmp.querySelector('.heritage-linked-colors__item, .grid__item');
        if (rec && rec.innerHTML.trim().length && hasItems) {
          pr.innerHTML = rec.innerHTML;
          wrap.classList.add('is-loaded');
        } else {
          wrap.remove(); // graceful: product has no linked colors
        }
      })
      .catch(function () { wrap.remove(); });
  }

  function activateThumb(mediaId, fullSrc) {
    var root = state.root;
    var main = root.querySelector('[data-qv-main]');
    if (main && fullSrc) { main.src = fullSrc; main.dataset.mediaId = mediaId || ''; }
    root.querySelectorAll('[data-qv-thumb]').forEach(function (t) {
      t.classList.toggle('is-active', t.dataset.mediaId === mediaId);
    });
  }

  function selectOption(position, value) {
    if (!state) return;
    state.selected[position - 1] = value;

    var match = state.variants.find(function (v) {
      return v.options.every(function (o, i) { return o === state.selected[i]; });
    });

    // Update button selected states for this option group
    state.root.querySelectorAll('[data-qv-opt-btn]').forEach(function (b) {
      if (parseInt(b.dataset.optionPosition, 10) === position) {
        b.classList.toggle('is-selected', b.dataset.optionValue === value);
      }
    });
    var currentLabel = state.root.querySelector('[data-qv-opt-current="' + position + '"]');
    if (currentLabel) currentLabel.textContent = value;

    if (match) {
      state.current = match;
      updatePrice();
      updateSku();
      updateAvailability();
      updateGalleryForVariant(match);
    }
  }

  function updateGalleryForVariant(variant) {
    if (!variant || !variant.featured_media) return;
    var mediaId = String(variant.featured_media.id);
    var thumb = state.root.querySelector('[data-qv-thumb][data-media-id="' + mediaId + '"]');
    if (thumb) {
      activateThumb(mediaId, thumb.dataset.full);
    } else if (variant.featured_media.preview_image) {
      var main = state.root.querySelector('[data-qv-main]');
      if (main) main.src = variant.featured_media.preview_image.src;
    }
  }

  function priceHtml(variant) {
    var fmt = state.moneyFormat;
    var orig = 0, final = 0, percent = 0, isCart = false;

    if (state.percent > 0 && state.isCart) {
      isCart = true;
      percent = state.percent;
      orig = variant.price;
      final = Math.round(variant.price * (100 - percent) / 100);
    } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      orig = variant.compare_at_price;
      final = variant.price;
      percent = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
    }

    if (orig > 0) {
      var noteHtml = (isCart && state.note)
        ? '<span class="heritage-pdp-price__note" style="color:' + state.color + ';">' + state.note + ' :</span>'
        : '';
      var pctHtml = percent > 0
        ? '<span class="heritage-pdp-price__percent">-%' + percent + '</span>'
        : '';
      return '<div class="price price--large heritage-pdp-price">' +
        '<div class="heritage-pdp-price__orig-line"><span class="heritage-pdp-price__orig">' + formatMoney(orig, fmt) + '</span></div>' +
        '<div class="heritage-pdp-price__sale-line">' + noteHtml +
        '<span class="heritage-pdp-price__final" style="color:' + state.color + ';">' + formatMoney(final, fmt) + '</span>' + pctHtml +
        '</div></div>';
    }
    return '<div class="price price--large heritage-pdp-price">' +
      '<div class="heritage-pdp-price__sale-line"><span class="heritage-pdp-price__final">' + formatMoney(variant.price, fmt) + '</span></div></div>';
  }

  function updatePrice() {
    var el = state.root.querySelector('[data-qv-price]');
    if (el && state.current) el.innerHTML = priceHtml(state.current);
  }

  function updateSku() {
    var wrap = state.root.querySelector('[data-qv-sku]');
    var val = state.root.querySelector('[data-qv-sku-value]');
    if (!wrap || !val) return;
    var sku = state.current && state.current.sku;
    if (sku) { val.textContent = sku; wrap.classList.remove('qv2--hidden'); }
    else { wrap.classList.add('qv2--hidden'); }
  }

  function updateAvailability() {
    var v = state.current;
    var atc = state.root.querySelector('[data-qv-atc]');
    var label = state.root.querySelector('[data-qv-atc-label]');
    var stock = state.root.querySelector('[data-qv-stock]');
    if (!v) return;
    if (v.available) {
      if (atc) { atc.disabled = false; atc.classList.remove('is-disabled'); }
      if (label) label.textContent = 'Sepete Ekle';
      if (stock) { stock.textContent = 'Stokta Var'; stock.classList.remove('qv2__stock--out'); }
    } else {
      if (atc) { atc.disabled = true; atc.classList.add('is-disabled'); }
      if (label) label.textContent = 'Tükendi';
      if (stock) { stock.textContent = 'Tükendi'; stock.classList.add('qv2__stock--out'); }
    }
  }

  // ---------------- Quantity ----------------
  function getQty() {
    var input = state.root.querySelector('[data-qv-qty]');
    var n = parseInt(input && input.value, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }
  function normalizeQty() {
    var input = state.root.querySelector('[data-qv-qty]');
    if (input) input.value = getQty();
  }
  function stepQty(delta) {
    var input = state.root.querySelector('[data-qv-qty]');
    if (!input) return;
    input.value = Math.max(1, getQty() + delta);
  }

  // ---------------- Favorites (localStorage) ----------------
  function readFavs() {
    try { return JSON.parse(localStorage.getItem(FAV_KEY)) || []; }
    catch (e) { return []; }
  }
  function writeFavs(list) {
    try { localStorage.setItem(FAV_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function syncFav(btn) {
    var handle = btn.dataset.favHandle;
    var active = readFavs().indexOf(handle) !== -1;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = btn.querySelector('.qv2__fav-label');
    if (label) label.textContent = active ? 'Favorilerde' : 'Favorilere Ekle';
  }
  function toggleFav(btn) {
    var handle = btn.dataset.favHandle;
    var list = readFavs();
    var idx = list.indexOf(handle);
    if (idx === -1) list.push(handle); else list.splice(idx, 1);
    writeFavs(list);
    syncFav(btn);
    document.dispatchEvent(new CustomEvent('favorites:changed', { detail: { handle: handle, favorites: list } }));
  }

  // ---------------- Add to cart ----------------
  function handleAddToCart() {
    if (!state || !state.current) return;
    var atc = state.root.querySelector('[data-qv-atc]');
    var label = state.root.querySelector('[data-qv-atc-label]');
    var original = label ? label.textContent : 'Sepete Ekle';
    if (atc) atc.disabled = true;
    if (label) label.textContent = 'Ekleniyor...';

    fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: state.current.id, quantity: getQty() }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (j) { throw new Error(j.description || 'Sepete eklenemedi'); });
      }
      return res.json();
    }).then(function (data) {
      refreshCartUI(data);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { variantId: state.current.id } }));
      if (label) label.textContent = original;
      if (atc) atc.disabled = false;
      closeModal();
      openCartDrawer();
    }).catch(function (err) {
      console.error('Quick view add to cart error:', err);
      if (label) label.textContent = err.message || 'Hata, tekrar dene';
      setTimeout(function () {
        if (label) label.textContent = original;
        if (atc) atc.disabled = false;
      }, 1800);
    });
  }

  function refreshCartUI(data) {
    if (!data || !data.sections) return;
    if (data.sections['cart-drawer']) {
      try {
        var tmp = document.createElement('div');
        tmp.innerHTML = data.sections['cart-drawer'];
        var incoming = tmp.querySelector('cart-drawer');
        var current = document.querySelector('cart-drawer');
        if (incoming && current) {
          current.classList.toggle('is-empty', incoming.classList.contains('is-empty'));
          current.innerHTML = incoming.innerHTML;
        }
      } catch (e) {}
    }
    if (data.sections['cart-icon-bubble']) {
      try {
        var tmp2 = document.createElement('div');
        tmp2.innerHTML = data.sections['cart-icon-bubble'];
        var inc = tmp2.querySelector('#cart-icon-bubble') || tmp2.querySelector('[id*="cart-icon-bubble"]') || tmp2.firstElementChild;
        var cur = document.querySelector('#cart-icon-bubble') || document.querySelector('[id*="cart-icon-bubble"]');
        if (inc && cur) cur.innerHTML = inc.innerHTML;
      } catch (e) {}
    }
  }

  function openCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    if (typeof cartDrawer.open === 'function') cartDrawer.open();
    else {
      cartDrawer.classList.add('active');
      cartDrawer.removeAttribute('hidden');
      document.body.classList.add('overflow-hidden');
    }
  }

  // ---------------- Entry ----------------
  function handleClick(handle) {
    var m = getModal();
    if (!m) return;
    openModal();
    els.content.hidden = true;
    els.content.innerHTML = '';
    els.loading.hidden = false;

    fetchSection(handle).then(function (html) {
      // The section endpoint returns the raw section HTML.
      els.content.innerHTML = html;
      els.loading.hidden = true;
      els.content.hidden = false;
      wireContent();
      // Move focus into the panel for a11y
      var closeBtn = els.content.querySelector('[data-qv-close]');
      if (closeBtn) closeBtn.focus();
      else if (els.panel) els.panel.focus();
    }).catch(function (err) {
      console.error('Quick view failed:', err);
      els.loading.innerHTML = '<p style="padding:2rem;text-align:center;">Ürün yüklenemedi. Lütfen detay sayfasını ziyaret edin.</p>';
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var handle = btn.dataset.quickView;
    if (handle) handleClick(handle);
  });
})();
